import mime from "mime"
import filesController from "../../api/controllers/files"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import {
    faFile,
    faFileAudio,
    faFileExcel,
    faFileImage,
    faFilePdf,
    faFileVideo,
    faFileWord
} from "@fortawesome/pro-solid-svg-icons"
import { UPLOAD_FILE_ERROR } from "../../components/Attachment/Attachment.const"
import { v4 as uuidv4 } from "uuid"

export enum DragEvent {
    Drag = "drag",
    DragEnd = "dragend",
    DragEnter = "dragenter",
    DragLeave = "dragleave",
    DragOver = "dragover",
    DragStart = "dragstart",
    Drop = "drop"
}

const imageType = "image"
const fileType = "file"

enum imageTypes {
    jpeg = ".jpeg",
    jpg = ".jpg",
    png = ".png",
    svg = ".svg"
}

export enum FileType {
    image = "image",
    video = "video",
    audio = "audio",
    other = "other"
}

export const specifyAttachmentType = (fileName: string) => {
    for (const type of Object.values(imageTypes)) {
        if (fileName.endsWith(type)) {
            return imageType
        }
    }

    return fileType
}

export const uploadFileToUrl = async (file: File, signal?: AbortSignal) =>
    filesController.uploadFileB64(
        {
            Name: file.name,
            Content: file
        },
        signal
    )

export const uploadFile = async (file: File, id?: string, signal?: AbortSignal): Promise<UploadedFile> => {
    const url = await uploadFileToUrl(file, signal)

    return {
        Id: id,
        Url: url,
        ContentType: specifyAttachmentType(file.name),
        Name: file.name,
        Size: file.size
    }
}

export type UploadedFile = {
    Id?: string
    Url: string
    ContentType: string
    Name: string
    Size: number
    AbortController?: AbortController
}

export const uploadFiles = async (files: File[]): Promise<UploadedFile[]> => {
    return await Promise.all(
        files.map(async file => {
            return await uploadFile(file)
        })
    )
}

export const prepareFilesWithProgressUploadParallel = async (
    files: FileList | File[],
    cb: (files: UploadedFile[]) => void
) => {
    const selectedFiles = Array.from(files)
    const completeFiles: UploadedFile[] = selectedFiles.map((file: File) => {
        const { itemType } = getFileInfo(file.name)

        return {
            Id: uuidv4(),
            Name: file.name,
            Size: file.size,
            Url: "",
            ContentType: itemType,
            AbortController: new AbortController()
        }
    })

    cb(completeFiles)

    // To keep files in same order
    let uploadedFiles: UploadedFile[] = completeFiles

    selectedFiles.forEach((file, index) => {
        const id = completeFiles[index].Id
        const abortFileController = completeFiles[index].AbortController

        if (!abortFileController) {
            return
        }

        uploadFile(file, id, abortFileController.signal)
            .then(uploadedFile => {
                const uploadedIndex = uploadedFiles.findIndex(fInfo => fInfo.Id === id)
                if (uploadedIndex !== -1) {
                    uploadedFiles[uploadedIndex] = uploadedFile
                    cb([...uploadedFiles])
                }
            })
            .catch(error => {
                if (error.code === "ERR_CANCELED") {
                    uploadedFiles = uploadedFiles.filter(fInfo => fInfo.Id !== id)
                } else {
                    const uploadedIndex = uploadedFiles.findIndex(fInfo => fInfo.Id === id)
                    uploadedFiles[uploadedIndex] = {
                        Url: UPLOAD_FILE_ERROR,
                        ContentType: file.type,
                        Name: file.name,
                        Size: file.size
                    }
                }

                cb([...uploadedFiles])
            })
    })

    return {
        uploadedFiles,
        completeFiles,
        abortUploadingFile: (id: string, updatedAttachments: UploadedFile[]) => {
            const AbortController = completeFiles.find(x => x.Id === id)?.AbortController

            AbortController?.abort()
            uploadedFiles = updatedAttachments
        }
    }
}

export const getFileExtFromUrl = (url: string): string => url.slice(url.lastIndexOf(".") + 1, url.length)

export const getFileExtension = (filename: string) => filename.split(".").pop() ?? ""

export const fileEndings = ["B", "KB", "MB", "GB", "TB"]

// Getting size in bytes
export const getBeautifiedFileSize = (size: number): string => {
    let resultStr = `${size} B`

    for (let i = 0; i < fileEndings.length; i++) {
        const currEndingIndex = fileEndings.length - i - 1
        const currSize = Math.floor(size / 1024 ** currEndingIndex)
        if (currSize > 0) {
            resultStr = `${currSize} ${fileEndings[currEndingIndex]}`
            return resultStr
        }
    }
    return resultStr
}

export const selectFileType = (mimeType: string): FileType => {
    switch (mimeType.split("/")[0]) {
        case "video":
            return FileType.video
        case "audio":
            return FileType.audio
        case "image":
            return FileType.image
        default:
            return FileType.other
    }
}

export const getFileInfo = (url: string): { itemType: FileType; fileExtension: string } => {
    const fileExtension = getFileExtFromUrl(url)
    const mimeType = mime.getType(fileExtension) ?? ""
    const itemType = selectFileType(mimeType)

    return {
        itemType,
        fileExtension
    }
}

enum DocFile {
    doc = "doc",
    docx = "docx",
    rtf = "rtf",
    xls = "xls",
    xlsx = "xlsx",
    pdf = "pdf"
}

const selectDocIcon = (fileExtension: string): IconDefinition => {
    switch (fileExtension) {
        case DocFile.doc:
        case DocFile.docx:
        case DocFile.rtf:
            return faFileWord
        case DocFile.xls:
        case DocFile.xlsx:
            return faFileExcel
        case DocFile.pdf:
            return faFilePdf
        default:
            return faFile
    }
}

export const selectFileIcon = (itemType: FileType, fileExtension: string): IconDefinition => {
    switch (itemType) {
        case FileType.other:
            return selectDocIcon(fileExtension)
        case FileType.image:
            return faFileImage
        case FileType.video:
            return faFileVideo
        case FileType.audio:
            return faFileAudio
    }
}

export const isBigFileSize = (
    file: File,
    limit: number // размер файла в мегабайтах
) => {
    const fileSize = file.size / (1024 * 1024)
    return limit > 0 && fileSize > limit
}
